import { buildStorageKeyForNamespace, resolveCacheNamespace } from '@/shared/data/cacheNamespace';

const STORAGE_KEY = 'flowqa-auth';
const SESSION_EXPIRED_KEY = 'flowqa-session-expired';
const POST_RESET_SUCCESS_KEY = 'flowqa-post-reset-success';
const DELETED_ACCOUNT_RECOVERY_KEY = 'flowqa-deleted-account-recovery';
const MANUAL_SIGNOUT_KEY = 'flowqa-manual-signout';
const SIGNOUT_TRANSITION_KEY = 'flowqa-signout-transition-at';
const CLERK_PROVISIONING_KEY = 'flowqa-clerk-provisioning';
const E2E_ACCESS_TOKEN_KEY = 'flowqa-e2e-access-token';
export const AUTH_BROADCAST_KEY = 'flowqa-auth-broadcast';
export const AUTH_BOOTSTRAP_KEY = 'flowqa-auth-bootstrap';
const ORG_FILTER_KEY = 'flowqa-organisation-filter';
const PROJECT_FILTER_KEY = 'flowqa-project-filter';
const SIGNOUT_TRANSITION_MAX_AGE_MS = 5000;
const POST_RESET_SUCCESS_MAX_AGE_MS = 5 * 60 * 1000;
const AUTH_BOOTSTRAP_MAX_AGE_MS = 30_000;

export type AuthBroadcastEvent = {
  type: 'signout' | 'session-expired';
  at: number;
};

export type ClerkProvisioningMode = 'signup' | 'restore';

export type AuthBootstrapEvent = {
  state: 'started' | 'succeeded' | 'failed';
  ownerId: string;
  at: number;
};

export type StoredSession = {
  tokens?: { accessToken: string };
  userId: string;
  orgId?: string | null;
  orgName?: string | null;
  expiresAt?: number;
  bootstrappedAt?: number;
  lastOrganisationId?: string | null;
  lastProjectId?: string | null;
  onboardingCompleted?: boolean;
  emailVerified?: boolean;
  roles?: string[];
  superuserEmailMatched?: boolean;
};

type JwtPayload = {
  sub?: string;
  userId?: string;
  user_id?: string;
  orgId?: string;
  org_id?: string;
  org?: string;
  orgName?: string;
  org_name?: string;
  roles?: string[];
  superuserEmailMatched?: boolean;
};

type LegacyStoredSession = StoredSession & {
  onboardingCompletedAt?: string | null;
  emailVerifiedAt?: string | null;
};

export const getE2eAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  const token = window.localStorage.getItem(E2E_ACCESS_TOKEN_KEY);
  if (!token) return null;
  return token;
};

export const clearE2eAccessToken = (): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(E2E_ACCESS_TOKEN_KEY);
};

const readFilterValue = (key: string, userId?: string | null): string | null => {
  if (typeof window === 'undefined') return null;
  const scopedKey = buildStorageKeyForNamespace(key, resolveCacheNamespace(userId));
  const value = window.localStorage.getItem(scopedKey) ?? window.localStorage.getItem(key);
  if (!value) return null;
  return value;
};

const writeFilterValue = (key: string, userId: string, value?: string | null): void => {
  if (typeof window === 'undefined') return;
  const scopedKey = buildStorageKeyForNamespace(key, resolveCacheNamespace(userId));
  if (value === undefined || value === null || value.length === 0) {
    window.localStorage.removeItem(scopedKey);
    return;
  }
  window.localStorage.setItem(scopedKey, value);
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1];
  if (!payload) return null;
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  try {
    const decoded = typeof atob === 'function' ? atob(padded) : '';
    if (!decoded) return null;
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
};

export const getStoredSession = (): StoredSession | null => {
  if (typeof window === 'undefined') return null;
  const e2eToken = getE2eAccessToken();
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    if (!e2eToken) return null;
    const payload = decodeJwtPayload(e2eToken);
    const userId = payload?.sub ?? payload?.userId ?? payload?.user_id;
    if (!userId) return null;
    const orgId = payload?.orgId ?? payload?.org_id ?? payload?.org ?? null;
    const orgName = payload?.orgName ?? payload?.org_name ?? null;
    const lastOrganisationId = readFilterValue(ORG_FILTER_KEY, userId) ?? orgId;
    const lastProjectId = readFilterValue(PROJECT_FILTER_KEY, userId);
    return {
      userId,
      orgId,
      orgName,
      lastOrganisationId,
      lastProjectId,
      onboardingCompleted: false,
      emailVerified: false,
      tokens: { accessToken: e2eToken },
      roles: payload?.roles,
      superuserEmailMatched: payload?.superuserEmailMatched ?? false,
    };
  }
  try {
    const parsed = JSON.parse(raw) as LegacyStoredSession;
    const lastOrganisationId =
      parsed.lastOrganisationId !== undefined
        ? parsed.lastOrganisationId
        : (readFilterValue(ORG_FILTER_KEY, parsed.userId) ?? parsed.orgId ?? null);
    const lastProjectId =
      parsed.lastProjectId !== undefined
        ? parsed.lastProjectId
        : readFilterValue(PROJECT_FILTER_KEY, parsed.userId);
    const onboardingCompleted = parsed.onboardingCompleted ?? Boolean(parsed.onboardingCompletedAt);
    const emailVerified = parsed.emailVerified ?? Boolean(parsed.emailVerifiedAt);
    const accessToken = parsed.tokens?.accessToken ?? e2eToken ?? undefined;
    return {
      ...parsed,
      ...(accessToken ? { tokens: { accessToken } } : {}),
      onboardingCompleted,
      emailVerified,
      lastOrganisationId,
      lastProjectId,
    };
  } catch {
    return null;
  }
};

const buildPersistedSession = (session: StoredSession, persistTokens: boolean): StoredSession => {
  const { tokens: _tokens, ...rest } = session;
  if (persistTokens && session.tokens) {
    return { ...rest, tokens: session.tokens };
  }
  return rest;
};

export const setStoredSession = (
  session: StoredSession | null,
  options?: { persistTokens?: boolean },
): void => {
  if (typeof window === 'undefined') return;
  if (session) {
    const persisted = buildPersistedSession(session, options?.persistTokens === true);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
};

export const setSessionExpiredFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.sessionStorage.setItem(SESSION_EXPIRED_KEY, String(Date.now()));
  } else {
    window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  }
};

export const getSessionExpiredFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.sessionStorage.getItem(SESSION_EXPIRED_KEY));
};

export const clearSessionExpiredFlag = (): void => {
  setSessionExpiredFlag(false);
};

export const setDeletedAccountRecoveryFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.sessionStorage.setItem(DELETED_ACCOUNT_RECOVERY_KEY, String(Date.now()));
    return;
  }
  window.sessionStorage.removeItem(DELETED_ACCOUNT_RECOVERY_KEY);
};

export const getDeletedAccountRecoveryFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.sessionStorage.getItem(DELETED_ACCOUNT_RECOVERY_KEY));
};

export const clearDeletedAccountRecoveryFlag = (): void => {
  setDeletedAccountRecoveryFlag(false);
};

export const broadcastAuthEvent = (event: AuthBroadcastEvent): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTH_BROADCAST_KEY, JSON.stringify(event));
};

export const parseAuthBroadcastEvent = (value: string | null): AuthBroadcastEvent | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<AuthBroadcastEvent>;
    if (
      (parsed.type === 'signout' || parsed.type === 'session-expired') &&
      typeof parsed.at === 'number'
    ) {
      return { type: parsed.type, at: parsed.at };
    }
  } catch {
    return null;
  }
  return null;
};

export const parseAuthBootstrapEvent = (value: string | null): AuthBootstrapEvent | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<AuthBootstrapEvent>;
    const parsedState = parsed.state;
    const isValidState =
      parsedState === 'started' || parsedState === 'succeeded' || parsedState === 'failed';
    if (
      isValidState &&
      typeof parsed.ownerId === 'string' &&
      parsed.ownerId.length > 0 &&
      typeof parsed.at === 'number'
    ) {
      return { state: parsedState, ownerId: parsed.ownerId, at: parsed.at };
    }
  } catch {
    return null;
  }
  return null;
};

export const getAuthBootstrapEvent = (): AuthBootstrapEvent | null => {
  if (typeof window === 'undefined') return null;
  const parsed = parseAuthBootstrapEvent(window.localStorage.getItem(AUTH_BOOTSTRAP_KEY));
  if (!parsed) return null;
  if (Date.now() - parsed.at <= AUTH_BOOTSTRAP_MAX_AGE_MS) {
    return parsed;
  }
  window.localStorage.removeItem(AUTH_BOOTSTRAP_KEY);
  return null;
};

export const setAuthBootstrapEvent = (event: AuthBootstrapEvent | null): void => {
  if (typeof window === 'undefined') return;
  if (event) {
    window.localStorage.setItem(AUTH_BOOTSTRAP_KEY, JSON.stringify(event));
  } else {
    window.localStorage.removeItem(AUTH_BOOTSTRAP_KEY);
  }
};

export const waitForAuthBootstrapEvent = async ({
  ownerId,
  timeoutMs = AUTH_BOOTSTRAP_MAX_AGE_MS,
}: {
  ownerId: string;
  timeoutMs?: number;
}): Promise<'none' | 'succeeded' | 'failed' | 'timed_out'> => {
  if (typeof window === 'undefined') return 'none';
  const current = getAuthBootstrapEvent();
  if (!current || current.ownerId === ownerId) {
    return 'none';
  }
  if (current.state === 'succeeded' || current.state === 'failed') {
    return current.state;
  }
  return await new Promise<'succeeded' | 'failed' | 'timed_out'>((resolve) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('storage', handleStorage);
      resolve('timed_out');
    }, timeoutMs);
    const finalize = (result: 'succeeded' | 'failed' | 'timed_out'): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      window.removeEventListener('storage', handleStorage);
      resolve(result);
    };
    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== AUTH_BOOTSTRAP_KEY) return;
      const next = parseAuthBootstrapEvent(event.newValue);
      if (!next || next.ownerId !== current.ownerId || next.at < current.at) {
        return;
      }
      if (next.state === 'succeeded' || next.state === 'failed') {
        finalize(next.state);
      }
    };
    window.addEventListener('storage', handleStorage);
  });
};

export const setPostResetSuccessFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.sessionStorage.setItem(POST_RESET_SUCCESS_KEY, String(Date.now()));
  } else {
    window.sessionStorage.removeItem(POST_RESET_SUCCESS_KEY);
  }
};

export const clearPostResetSuccessFlag = (): void => {
  setPostResetSuccessFlag(false);
};

export const getPostResetSuccessFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  const rawValue = window.sessionStorage.getItem(POST_RESET_SUCCESS_KEY);
  if (!rawValue) {
    return false;
  }
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    window.sessionStorage.removeItem(POST_RESET_SUCCESS_KEY);
    return false;
  }
  if (Date.now() - parsed > POST_RESET_SUCCESS_MAX_AGE_MS) {
    window.sessionStorage.removeItem(POST_RESET_SUCCESS_KEY);
    return false;
  }
  return true;
};

export const setManualSignOutFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.localStorage.setItem(MANUAL_SIGNOUT_KEY, String(Date.now()));
  } else {
    window.localStorage.removeItem(MANUAL_SIGNOUT_KEY);
  }
};

export const setSignOutTransitionFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.localStorage.setItem(SIGNOUT_TRANSITION_KEY, String(Date.now()));
  } else {
    window.localStorage.removeItem(SIGNOUT_TRANSITION_KEY);
  }
};

export const setClerkProvisioningFlag = (value: boolean | ClerkProvisioningMode): void => {
  if (typeof window === 'undefined') return;
  if (value === false) {
    window.sessionStorage.removeItem(CLERK_PROVISIONING_KEY);
    return;
  }
  if (value === true) {
    window.sessionStorage.setItem(CLERK_PROVISIONING_KEY, 'signup');
    return;
  }
  window.sessionStorage.setItem(CLERK_PROVISIONING_KEY, value);
};

export const getClerkProvisioningFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return getClerkProvisioningMode() !== null;
};

export const getClerkProvisioningMode = (): ClerkProvisioningMode | null => {
  if (typeof window === 'undefined') return null;
  const rawValue = window.sessionStorage.getItem(CLERK_PROVISIONING_KEY);
  if (rawValue === 'signup' || rawValue === 'restore') {
    return rawValue;
  }
  if (rawValue === '1') {
    return 'signup';
  }
  if (rawValue !== null) {
    window.sessionStorage.removeItem(CLERK_PROVISIONING_KEY);
  }
  return null;
};

export const getManualSignOutFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.localStorage.getItem(MANUAL_SIGNOUT_KEY));
};

export const getSignOutTransitionFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  const rawValue = window.localStorage.getItem(SIGNOUT_TRANSITION_KEY);
  if (!rawValue) return false;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    window.localStorage.removeItem(SIGNOUT_TRANSITION_KEY);
    return false;
  }
  if (Date.now() - parsed > SIGNOUT_TRANSITION_MAX_AGE_MS) {
    window.localStorage.removeItem(SIGNOUT_TRANSITION_KEY);
    return false;
  }
  return true;
};

export const updateSessionContext = (
  lastOrganisationId?: string | null,
  lastProjectId?: string | null,
): void => {
  const current = getStoredSession();
  if (!current) return;
  const next: StoredSession = { ...current };
  if (lastOrganisationId !== undefined) {
    next.lastOrganisationId = lastOrganisationId;
  }
  if (lastProjectId !== undefined) {
    next.lastProjectId = lastProjectId;
  }
  setStoredSession(next);
  if (lastOrganisationId !== undefined) {
    writeFilterValue(ORG_FILTER_KEY, current.userId, lastOrganisationId);
  }
  if (lastProjectId !== undefined) {
    writeFilterValue(PROJECT_FILTER_KEY, current.userId, lastProjectId);
  }
};

export const getStoredContext = (): {
  lastOrganisationId: string | null;
  lastProjectId: string | null;
} => {
  const current = getStoredSession();
  const userId = current?.userId ?? null;
  const persistedOrganisationId = readFilterValue(ORG_FILTER_KEY, userId);
  const persistedProjectId = readFilterValue(PROJECT_FILTER_KEY, userId);
  return {
    lastOrganisationId: persistedOrganisationId ?? current?.lastOrganisationId ?? null,
    lastProjectId: persistedProjectId ?? current?.lastProjectId ?? null,
  };
};

export { STORAGE_KEY };
